import {ErrorDetail} from 'gen-ts-googleapis';
import React, {useEffect} from 'react';
import {Mode, useForm} from 'react-hook-form';
import {getGrpcFieldViolationsAsFormError} from '../../../utils/grpc/grpcFieldViolations';
import {useBankDetailsTranslationContext} from '../context/CodBankDataTranslation.context';
import {
  CODPaymentInformation,
  UpdateOrCreateBankInformation,
} from './useCodPaymentInformation';

export type SaveBankDataStatus = 'idle' | 'success' | 'error' | 'progress';

export interface UpdateBankDataFormState {
  firstName: string;
  lastName: string;
  iban: string;
  orderId?: number;
}

const initialFormValues = {
  firstName: '',
  lastName: '',
  iban: '',
  orderId: undefined,
};

type IbanError =
  | 'STRING_TOO_LONG'
  | 'STRING_TOO_SHORT'
  | 'INVALID_FORMAT'
  | 'IBAN_COUNTRY_MISMATCH';

const useBankDataForm = (
  updateOrCreateBankInformation: UpdateOrCreateBankInformation,
  codPaymentInformation?: CODPaymentInformation | null,
  mode: Mode = 'onSubmit',
  disableLazyUpdate?: boolean,
) => {
  const [submitSuccess, setSubmitSuccess] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<Error | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveBankDataStatus, setSaveBankDataStatus] =
    React.useState<SaveBankDataStatus>('idle');
  const {formState: translationFormState} = useBankDetailsTranslationContext();
  const resetSaveBankDataStatus = () => {
    setSaveBankDataStatus('idle');
  };

  const {
    handleSubmit,
    register,
    reset,
    errors,
    getValues,
    formState,
    control,
    setValue,
    setError,
    watch,
  } = useForm<UpdateBankDataFormState>({
    defaultValues: {
      ...initialFormValues,
      firstName: codPaymentInformation?.bankData.firstName,
      lastName: codPaymentInformation?.bankData.lastName,
      iban: codPaymentInformation?.bankData.iban,
    },
    mode,
  });

  useEffect(() => {
    if (
      codPaymentInformation?.bankData.firstName &&
      codPaymentInformation?.bankData.lastName &&
      codPaymentInformation?.bankData.iban &&
      !disableLazyUpdate
    ) {
      reset({
        firstName: codPaymentInformation.bankData.firstName,
        lastName: codPaymentInformation.bankData.lastName,
        iban: codPaymentInformation?.bankData.iban,
      });
    }
  }, [codPaymentInformation, reset]);

  const submitHandler = React.useCallback(
    async (data: UpdateBankDataFormState) => {
      setIsSaving(true);
      setSubmitSuccess(false);
      setSubmitError(null);
      setSaveBankDataStatus('progress');

      try {
        const paymentMethod = await updateOrCreateBankInformation(data);

        if (paymentMethod) {
          setSubmitSuccess(true);
          setSubmitSuccess(true);
          setSubmitError(null);
          setIsSaving(false);
          setSaveBankDataStatus('success');
        } else {
          setSubmitSuccess(false);
          setSubmitError(new Error('failed to update bank information'));
          setIsSaving(false);
          setSaveBankDataStatus('error');
        }
      } catch (error) {
        setSubmitSuccess(false);
        setSubmitError(error);
        setIsSaving(false);
        setSaveBankDataStatus('error');
        getGrpcFieldViolationsAsFormError(error.details as ErrorDetail[], [
          'iban',
        ]).forEach(field => {
          const ibanFieldViolation: {field: string; description: string} =
            error.details[0].fieldViolations.find(
              (violation: {field: string; description: string}) =>
                violation.field === 'iban',
            );
          const description = ibanFieldViolation.description as IbanError;
          const message =
            translationFormState.iban.messages[description] ||
            translationFormState.iban.messages.invalid;
          setError(field as 'iban', {
            type: 'field',
            message,
          });
        });
      }
    },
    [updateOrCreateBankInformation, setError, translationFormState],
  );

  return {
    formState,
    initialFormValues,
    submitHandler,
    isSaveSuccess: submitSuccess,
    submitError,
    isSavingData: isSaving,
    register,
    errors,
    reset,
    handleSubmit,
    getValues,
    control,
    setValue,
    saveBankDataStatus,
    resetSaveBankDataStatus,
    watch,
  };
};

export default useBankDataForm;
